import { useSelector } from 'react-redux'
import {
  useTournamentLeaderboard,
  useTournamentLeaderboardHistory,
} from '@hooks/queries/useTournaments'
import { TOURNAMENT_ORIGIN, REWARD_TYPE } from '@constants/tournamentConstants'
import { splitLeaderboard } from '@core/tournaments/leaderboardView'
import SpinningLoader from '../../../../../components/loaders/SpinningLoader'
import LeaderboardBar from './LeaderboardBar'
import LeaderboardRow from './LeaderboardRow'

// DefenderV2 fetches the whole leaderboard in one shot (no page/size params,
// no pagination UI) — a generous fixed size approximates that instead of
// exposing page controls that don't exist in the reference design.
const LEADERBOARD_FETCH_SIZE = 100

// Top-3 gradient bars + a table for the rest (ranks 4+, or the viewer bumped
// to the front — see leaderboardView.js). Not built on the generic
// TournamentsList shell: this shape (bars + table) isn't a flat card list.
const TournamentLeaderboardTab = ({ tournament, origin }) => {
  const isHistory = origin === TOURNAMENT_ORIGIN.HISTORY
  const playerId = useSelector((state) => state.session.details.playerId)
  const currencyCode = useSelector((state) => state.wallet.displayCurrencyCode)

  const active = useTournamentLeaderboard(
    { tournamentId: tournament.id, page: 1, pageSize: LEADERBOARD_FETCH_SIZE },
    { enabled: !isHistory }
  )
  const history = useTournamentLeaderboardHistory(
    { tournamentId: tournament.id, page: 1, pageSize: LEADERBOARD_FETCH_SIZE },
    { enabled: isHistory }
  )

  const { data, isPending, isError } = isHistory ? history : active
  const rows = data?.rows ?? []
  const { top3, others } = splitLeaderboard(rows, playerId)

  const isBetsCount = tournament.type === 'NUMBER_OF_BETS'
  const pointsLabel = isBetsCount ? 'Bets' : currencyCode
  const rewardHeaderLabel =
    tournament.rewardType === REWARD_TYPE.FREEBET ? 'Reward (Freebets)' : `Reward (${currencyCode})`

  const columns = [
    { key: 'rank', label: 'Position' },
    { key: 'playerId', label: 'Player ID' },
    { key: 'points', label: isBetsCount ? 'No. of Bets' : 'Stake', currency: isBetsCount ? null : currencyCode },
    ...(isHistory
      ? [
          {
            key: tournament.rewardType === REWARD_TYPE.FREEBET ? 'freeBets' : 'reward',
            label: rewardHeaderLabel,
            isReward: true,
          },
        ]
      : []),
  ]

  if (isPending) {
    return (
      <div className="standard-normal-tournament-leaderboard-outer-container">
        <div className="standard-normal-tournament-leaderboard-loading-container">
          <SpinningLoader />
        </div>
      </div>
    )
  }

  if (isError || !rows.length) {
    return (
      <div className="standard-normal-tournament-leaderboard-outer-container">
        <div className="standard-normal-tournament-leaderboard-empty-container">
          {isError ? 'Ooops! Something Went Wrong!' : 'No Leaderboard Found for this Tournament'}
        </div>
      </div>
    )
  }

  return (
    <div className="standard-normal-tournament-leaderboard-outer-container">
      <div className="standard-normal-tournament-leaderboard-inner-container">
        <div className="standard-normal-leaderboard-bars-container">
          {top3[1] && (
            <LeaderboardBar rank={2} player={top3[1]} pointsLabel={pointsLabel} showReward={isHistory} />
          )}
          {top3[0] && (
            <LeaderboardBar rank={1} player={top3[0]} pointsLabel={pointsLabel} showReward={isHistory} />
          )}
          {top3[2] && (
            <LeaderboardBar rank={3} player={top3[2]} pointsLabel={pointsLabel} showReward={isHistory} />
          )}
        </div>

        {others.length > 0 && (
          <div className="standard-normal-leaderboard-table-container">
            <div className="standard-normal-leaderboard-table-header-row">
              {columns.map((col) => (
                <div key={col.key} className="standard-normal-leaderboard-table-header-element">
                  {col.label}
                  {col.currency && <span>({col.currency})</span>}
                </div>
              ))}
            </div>
            <div className="standard-normal-leaderboard-table-body no-scrollbar">
              {others.map((row, index) => (
                <LeaderboardRow
                  key={row.playerId ?? index}
                  row={row}
                  columns={columns}
                  currencyCode={currencyCode}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default TournamentLeaderboardTab
